import { Server } from './server';
import { Service, ServiceResponse } from './service';

const FORUMS_CACHE_EXPIRATION = 60*60;
const TOPICS_CACHE_EXPIRATION = 60*60*24;   
const POSTS_CACHE_EXPIRATION = 60*15;

export class ForumsService extends Service {
  constructor() {
    super();
  }

  public async init(): Promise<ServiceResponse> {
    await Server.database.purge('FORUMS', FORUMS_CACHE_EXPIRATION);
    await Server.database.purge('FORUMTOPICS#', TOPICS_CACHE_EXPIRATION);
    await Server.database.purge('FORUMTOPICPOSTS#', POSTS_CACHE_EXPIRATION);
    return {success: true};
  }

  public getLastReadDate() {
    if(!Server.account.isLoggedIn())
      return null;
    let lastReadForumDateCookie = Server.user.getCookie('LastForumsRead');
    return lastReadForumDateCookie ? new Date(lastReadForumDateCookie) : null;
  }

  public setLastReadDate(dateStr:string) {
    let cookieDate = this.getLastReadDate();
    let date = new Date(dateStr);
    if(!cookieDate || date > cookieDate)
      Server.user.setCookie({key: 'LastForumsRead', value: dateStr});
  }

  // cache
  public async setForumsCache(forums:any[]) {
    await Server.database.putItem(`FORUMS`, forums);
  }

  public async getForumsCache() {
    return await Server.database.getItem(`FORUMS`, FORUMS_CACHE_EXPIRATION);
  }

  public async clearForumsCache() {
    await Server.database.removeItem(`FORUMS`);
  }
  
  public async setTopicsCache(forumId:string, topics:any[], update:boolean = false) {
    await Server.database.putItem(`FORUMTOPICS#${forumId}`, topics, update);
  }

  public async getTopicsCache(forumId:string) {
    return Server.database.getItem(`FORUMTOPICS#${forumId}`, TOPICS_CACHE_EXPIRATION);
  }

  public async clearTopicsCache(forumId:string) {
    await Server.database.removeItem(`FORUMTOPICS#${forumId}`);
  }

  public async setPostsCache(forumId:string, topicId:string, sort:boolean, cache:any, update:boolean = false) {
    await Server.database.putItem(`FORUMTOPICPOSTS#${forumId}#${topicId}#${sort}`, cache, update);
  }

  public async getPostsCache(forumId:string, topicId:string, sort:boolean) {
    return Server.database.getItem(`FORUMTOPICPOSTS#${forumId}#${topicId}#${sort}`, POSTS_CACHE_EXPIRATION);
  }

  public async clearPostsCache(forumId:string, topicId:string) {
    await Server.database.purge(`FORUMTOPICPOSTS#${forumId}#${topicId}`);
  }

  // forum services
  public async createForum(title: string, description: string) {
    let response = await this.sendCommand('forums', 'create-forum', {title, description});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async editForum(forum:string, title: string, description: string) {
    let response = await this.sendCommand('forums', 'edit-forum', {forum, title, description});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async archiveForum(forum:string) {
    let response = await this.sendCommand('forums', 'archive-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async restoreForum(forum:string) {
    let response = await this.sendCommand('forums', 'restore-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async hideForum(forum:string) {
    let response = await this.sendCommand('forums', 'hide-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async showForum(forum:string) {
    let response = await this.sendCommand('forums', 'show-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async getForums() {
    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let response = await this.sendCommand(method, 'get-forums');

    if(response.status != 200)
      return {success: false, message: response.body.message};

    let forums = response.body.forums;

    forums.sort((a:any, b:any)=>{
      if(a.archived && !b.archived)
        return 1;
      else if(!a.archived && b.archived)
        return -1;

      if(a.recent && !b.recent)
        return -1;
      else if(!a.recent && b.recent)
        return 1;

      if(a.recent && b.recent) {
        if(a.recent.date && !b.recent.date)
          return -1;
        else if(!a.recent.date && b.recent.date)
          return 1;

        let adate = new Date(a.recent.date);
        let bdate = new Date(b.recent.date);

        if(adate > bdate)
          return -1;
        else if(adate < bdate)
          return 1;
      }

      if(a.title < b.title)
        return -1;
      else if(a.title > b.title)
        return 1;

      return 0;
    });

    return {success: true, forums};
  }

  public async getForum(forum:string) {
    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let response = await this.sendCommand(method, 'get-forum', {forum});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, forum: response.body.forum};
  }

  public async deleteForum(forum:string) {
    let response = await this.sendCommand('forums', 'delete-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async undeleteForum(forum:string) {
    let response = await this.sendCommand('forums', 'undelete-forum', {forum});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  // topic services
  public async createTopic(forum:string, title: string, content: string, notify:boolean=false) {
    let response = await this.sendCommand('forums', 'create-topic', {forum, title, content, notify});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, topic: response.body.topic};
  }

  public async editTopic(forum:string, topic:string, title: string) {
    let response = await this.sendCommand('forums', 'edit-topic', {forum, topic, title});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async archiveTopic(forum:string, topic:string) {
    let response = await this.sendCommand('forums', 'archive-topic', {forum, topic});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async restoreTopic(forum:string, topic:string) {
    let response = await this.sendCommand('forums', 'restore-topic', {forum, topic});
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true};
  }

  public async getTopics(forum:string, params:any={}) {
    params.forum = forum;

    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let command = Server.account.isLoggedIn() ? 'get-topics' : 'get-forum-topics';
    let response = await this.sendCommand(method, command, params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, topics: response.body.topics};
  }

  public async getTopicsInfo(forum:string, topics:any[]) {
    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let command = Server.account.isLoggedIn() ? 'get-topics-info' : 'get-forum-topics-info';
    let response = await this.sendCommand(method, command, {forum, topics});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, topics: response.body.topics};
  }

  public async getTopic(forum:string, topic:string) {
    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let command = Server.account.isLoggedIn() ? 'get-topic' : 'get-forum-topic';
    let response = await this.sendCommand(method, command, {forum, topic});

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, topic: response.body.topic};
  }

  // posts services
  public async createPost(forum:string, topic:string, content: string) {
    let response = await this.sendCommand('forums', 'create-post', {forum, topic, content});
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, date: response.body.date};
  }

  public async editPost(forum:string, topic:string, date:string, content:string, user:string=null) {
    let params:any = {forum, topic, date, content};
    if(user)
      params.user = user;

    let response = await this.sendCommand('forums', 'edit-post', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true};
  }

  public async deletePost(forum:string, topic:string, date:string, user:string=null) {
    let params:any = {forum, topic, date};
    if(user)
      params.user = user;

    let response = await this.sendCommand('forums', 'delete-post', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true};
  }

  public async getPosts(forum:string, topic:string, params:any = {}) {
    params.forum = forum;
    params.topic = topic;

    let method = Server.account.isLoggedIn() ? 'forums' : 'public';
    let command = Server.account.isLoggedIn() ? 'get-posts' : 'get-forum-topic-posts';
    let response = await this.sendCommand(method, command, params);

    if(response.status != 200)
      return {success: false, message: response.body.message};

    return {success: true, posts: response.body.posts, more: response.body.more};
  }

  public async getPost(forum:string, topic:string, date:string, user:string=null) {
    let params:any = {forum, topic, date};
    if(user)
      params.user = user;

    let response = await this.sendCommand('forums', 'get-post', params);
    if(response.status != 200)
      return {success: false, message: response.body.message};
    return {success: true, post: response.body.post};
  }
}